import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isEqual } from 'lodash';
import { Filter, FilterWrite, SetFilterValueOptions } from './filter-types';
import { useFilterClears } from './use-filter-clears';

const isKeyForFilter = (filter: Filter, key: string): boolean => {
  if (filter.id === key) {
    return true;
  }

  if (filter.type === 'dateRange') {
    return [filter.startKey, filter.endKey].includes(key);
  }

  if (filter.type === 'range') {
    return [filter.minKey, filter.maxKey].includes(key);
  }

  if (filter.type === 'tags') {
    return [
      filter.tagKeyAttribute,
      filter.tagValueAttribute,
      filter.tagOperatorAttribute,
    ].includes(key);
  }

  if (filter.type === 'combo' && filter.filters) {
    return filter.filters.some((subFilter) => isKeyForFilter(subFilter, key));
  }

  return false;
};

const buildFilterState = (
  search: URLSearchParams,
  filters: Filter[],
): { [key: string]: unknown } => {
  const state: { [key: string]: unknown } = {};

  const processFilter = (filter: Filter) => {
    if (filter.type === 'dateRange') {
      state[filter.startKey] = search.get(filter.startKey);
      state[filter.endKey] = search.get(filter.endKey);
    } else if (filter.type === 'range') {
      state[filter.minKey] = search.get(filter.minKey);
      state[filter.maxKey] = search.get(filter.maxKey);
    } else if (filter.type === 'select' && filter.multiple) {
      state[filter.id] = search.getAll(filter.id);
    } else if (['status', 'criticalStatus', 'select'].includes(filter.type)) {
      state[filter.id] = search.get(filter.id);
    } else if (filter?.type === 'tags') {
      state[filter.tagKeyAttribute] = search.get(filter.tagKeyAttribute);
      state[filter.tagValueAttribute] = search.get(filter.tagValueAttribute);
      state[filter.tagOperatorAttribute] = search.get(
        filter.tagOperatorAttribute,
      );
    } else {
      state[filter.id] = search.getAll(filter.id);
    }
  };

  filters.forEach((filter) => {
    if (filter?.type === 'combo') {
      // An absent combo reads as the first sub-filter with a value in the URL,
      // else the first sub-filter, without the URL carrying it, so readers
      // see a string before the user picks one.
      const subFilterInSearch = filter.filters?.find((subFilter) =>
        Array.from(search.keys()).some((key) => isKeyForFilter(subFilter, key)),
      );
      state[filter.id] =
        search.get(filter.id) ||
        subFilterInSearch?.id ||
        filter.filters?.[0]?.id;
      filter.filters?.forEach(processFilter);
    } else {
      processFilter(filter);
    }
  });

  return state;
};

/** The visible filters plus any hidden one the search sets a value for. */
const addFiltersInSearch = (
  currentFilters: string[],
  search: URLSearchParams,
  filters: Filter[],
) => {
  const newFilters = [...currentFilters];

  search.forEach((_, key) => {
    const filter = filters.find(
      (filter) =>
        isKeyForFilter(filter, key) && !currentFilters.includes(filter.id),
    );

    if (filter && !newFilters.includes(filter.id)) {
      newFilters.push(filter.id);
    }
  });

  const uniqueNewFilters = Array.from(new Set(newFilters));
  return isEqual(currentFilters, uniqueNewFilters)
    ? currentFilters
    : uniqueNewFilters;
};

export const useFilterQueryMode = ({ filters }: { filters: Filter[] }) => {
  const location = useLocation();
  const search = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const preSelectedFilters = filters
    .filter((f) => f.preSelected || f.permanent)
    .map((f) => f.id);
  const navigate = useNavigate();
  // The search last navigated to, so writes before the next render compare
  // against it rather than the location React Router has not yet rendered.
  // It applies only while the router still holds the location it was written
  // under, so Back or an outside navigation never leaves a stale target
  // behind. The location object is compared, not its key, because the first
  // entry of a session and any entry made outside the router share a key.
  const lastNavigated = useRef<{
    location: typeof location;
    to: string;
  }>(null);
  // The location of the latest render. A setter captured in an earlier render
  // must compare against this, not the location it closed over.
  const latestLocation = useRef(location);
  // A layout effect runs before every passive effect and event handler of the
  // commit, so a setter they call sees the committed location.
  useLayoutEffect(() => {
    latestLocation.current = location;
  });
  // True until the first commit's effects have all run, which is when defaults
  // written by filters mounting on page load happen. Filters added later are
  // user steps. The flip waits for a microtask because the caller's own effects
  // run after this hook's, in the same commit, and must still see page load.
  const isPageLoad = useRef(true);
  useEffect(() => {
    queueMicrotask(() => {
      isPageLoad.current = false;
    });
  }, []);
  const [visibleFilters, setFiltersVisible] =
    useState<string[]>(preSelectedFilters);
  const { clearCounts, noteWrite } = useFilterClears();
  // Seed filterValues from the URL immediately so child components (e.g.
  // ComboFilter) read the correct state on their first render.
  const [filterValues, setFilterValues] = useState<{ [key: string]: unknown }>(
    () =>
      buildFilterState(new URLSearchParams(window.location.search), filters),
  );

  // The URL is the source of truth, so the values follow it during render.
  // They are replaced only when a value differs, which keeps their identity
  // stable for callers whose effects depend on the whole object.
  const urlFilterValues = buildFilterState(search, filters);
  const isOutOfSyncWithUrl = Object.keys(urlFilterValues).some(
    (key) => !isEqual(urlFilterValues[key], filterValues[key]),
  );
  if (isOutOfSyncWithUrl) {
    setFilterValues(urlFilterValues);
  }

  // A filter the URL sets a value for is shown, checked whenever the search
  // or the set of filters changes. Callers rebuild the filters array on
  // every render, so the check keys on their ids rather than the array.
  const visibilitySource = `${location.search}|${filters
    .map(({ id }) => id)
    .join(',')}`;
  const [checkedVisibilitySource, setCheckedVisibilitySource] =
    useState<string>();
  if (visibilitySource !== checkedVisibilitySource) {
    setCheckedVisibilitySource(visibilitySource);
    setFiltersVisible((currentFilters) =>
      addFiltersInSearch(currentFilters, search, filters),
    );
  }

  // Skips a write that leaves the URL as it is, so it adds no history entry.
  // Setting a value moves its key to the end, so key order is not compared.
  const navigateToSearch = (
    nextSearch: URLSearchParams,
    { replace, replaceOnPageLoad }: SetFilterValueOptions = {},
  ) => {
    const sorted = (query: string) => {
      const params = new URLSearchParams(query);
      params.sort();
      return params.toString();
    };
    const next = nextSearch.toString();
    const live = latestLocation.current;
    const current =
      lastNavigated.current?.location === live
        ? lastNavigated.current.to
        : live.search;
    if (sorted(next) === sorted(current)) {
      return;
    }
    lastNavigated.current = { location: live, to: next };
    const shouldReplace = replace || (replaceOnPageLoad && isPageLoad.current);
    // A replace rewrites the entry it stands on, so it keeps that entry's
    // state and hash. A push starts a new entry without them.
    navigate(
      shouldReplace
        ? {
            pathname: window.location.pathname,
            search: `?${next}`,
            hash: live.hash,
          }
        : `${window.location.pathname}?${next}`,
      shouldReplace ? { replace: true, state: live.state } : { replace: false },
    );
  };

  const writeToSearch = (
    id: string,
    value: string | string[] | null | undefined,
    filter?: Filter,
  ) => {
    noteWrite(id, value);
    // A range sets both bounds together. Its value is `[lower, upper]`; an
    // empty bound is unset.
    if (filter?.type === 'range') {
      const [lower, upper] = Array.isArray(value) ? value : [];
      search.delete(filter.minKey);
      search.delete(filter.maxKey);
      if (lower) {
        search.set(filter.minKey, lower);
      }
      if (upper) {
        search.set(filter.maxKey, upper);
      }
      return;
    }
    if (filter?.type === 'dateRange' && !value) {
      search.delete(filter.startKey);
      search.delete(filter.endKey);
    } else if (filter?.type === 'tags' && !value && id === filter.id) {
      search.delete(filter.tagKeyAttribute);
      search.delete(filter.tagValueAttribute);
      search.delete(filter.tagOperatorAttribute);
    } else if (filter?.type === 'combo' && !value) {
      search.delete(id);
      filter.filters?.forEach((subFilter) => {
        if (subFilter.type === 'dateRange') {
          search.delete(subFilter.startKey);
          search.delete(subFilter.endKey);
        } else if (subFilter.type === 'tags') {
          search.delete(subFilter.tagKeyAttribute);
          search.delete(subFilter.tagValueAttribute);
          search.delete(subFilter.tagOperatorAttribute);
        } else {
          search.delete(subFilter.id);
        }
      });
    } else {
      search.delete(id);
    }
    if (value) {
      if (typeof value === 'string') {
        search.append(id, value);
      } else {
        value.map((item) => search.append(id, item));
      }
    }
  };

  const setFilterValue = (
    id: string,
    value: string | string[] | null | undefined,
    filter?: Filter,
    options?: SetFilterValueOptions,
  ) => {
    writeToSearch(id, value, filter);
    navigateToSearch(search, options);
  };

  // Applies every write, then navigates once, so a change that touches several
  // filters is one history entry.
  const applyFilterWrites = (
    writes: FilterWrite[],
    options?: SetFilterValueOptions,
  ) => {
    writes.forEach(({ id, value, filter }) => writeToSearch(id, value, filter));
    navigateToSearch(search, options);
  };

  const setVisibleFilters = (value: string[]) => {
    if (value.length < 1) {
      filters.map((filter) => {
        setFilterValue(filter.id, undefined, filter);
      });
      setFiltersVisible([]);
    } else {
      setFiltersVisible((oldFilters) => {
        const removedFilters = oldFilters.filter(
          (filter) => !value.includes(filter),
        );
        removedFilters.map((filterId) => {
          setFilterValue(
            filterId,
            undefined,
            filters.find((filter) => filter.id === filterId),
          );
        });
        return value;
      });
    }
  };

  const removeFilter = (filter?: Filter) => {
    setFiltersVisible((filters) => {
      const index = filters.findIndex((x) => x === filter?.id);
      if (index >= 0) {
        const removedFilters = filters.splice(index, 1);
        removedFilters.map((value) => {
          setFilterValue(value, undefined, filter);
        });
      }
      return [...filters];
    });
  };

  return {
    setFilterValue,
    applyFilterWrites,
    setVisibleFilters,
    visibleFilters,
    filterValues,
    clearCounts,
    removeFilter,
    setFilterValues,
  };
};
